import { useSearchParams } from "next/navigation";
import { useEffect, useState } from "react";
import { shallow } from "zustand/shallow";

import { useOverlayCalendarStore } from "@calcom/features/bookings/Booker/components/OverlayCalendar/store";
import { useBookerStore } from "@calcom/features/bookings/Booker/store";
import type { ToggledConnectedCalendars } from "@calcom/features/bookings/Booker/types";
import { useTimePreferences } from "@calcom/features/bookings/lib";
import { localStorage } from "@calcom/lib/webstorage";
import { trpc } from "@calcom/trpc/react";

type UseCalendarsProps = {
  hasSession: boolean;
};

export const useCalendars = ({ hasSession }: UseCalendarsProps) => {
  const searchParams = useSearchParams();
  const selectedDate = useBookerStore((state) => state.selectedDate);
  const { timezone } = useTimePreferences();

  const setGuestCalendarConnected = useOverlayCalendarStore((state) => state.setGuestCalendarConnected);
  const guestCalendarConnected = useOverlayCalendarStore((state) => state.guestCalendarConnected);

  const [guestBusyDates, setGuestBusyDates] = useState<Array<{ start: string; end: string }>>([]);

  const calendarConnectedParam = searchParams?.get("calendarConnected");

  // For guests: on mount and whenever ?calendarConnected=true appears (post-OAuth redirect),
  // check the httpOnly _schedly_gcal cookie by calling /api/guest-calendar/slots.
  // If connected, load their busy times and enable the overlay.
  useEffect(() => {
    if (hasSession) return;

    const fetchGuestBusy = async () => {
      const start = new Date();
      start.setDate(1);
      const end = new Date(start);
      end.setMonth(end.getMonth() + 3);

      try {
        const res = await fetch(
          `/api/guest-calendar/slots?start=${encodeURIComponent(start.toISOString())}&end=${encodeURIComponent(end.toISOString())}`
        );
        if (res.ok) {
          const data = await res.json();
          if (data.connected) {
            setGuestBusyDates(data.busyTimes || []);
            setGuestCalendarConnected(true);
            localStorage.setItem("overlayCalendarSwitchDefault", "true");
          } else {
            setGuestCalendarConnected(false);
          }
        } else {
          setGuestCalendarConnected(false);
        }
      } catch {
        setGuestCalendarConnected(false);
      }
    };

    fetchGuestBusy();
    // Re-fetch when the calendarConnected param changes (post-OAuth return)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSession, calendarConnectedParam]);

  const switchEnabled =
    searchParams?.get("overlayCalendar") === "true" ||
    localStorage?.getItem("overlayCalendarSwitchDefault") === "true";

  const [set, setSet] = useState<Set<ToggledConnectedCalendars>>(() => {
    const storedValue = localStorage.getItem("toggledConnectedCalendars");
    return storedValue ? new Set(JSON.parse(storedValue)) : new Set([]);
  });

  const [calendarSettingsOverlay] = useOverlayCalendarStore(
    (state) => [state.calendarSettingsOverlayModal, state.setCalendarSettingsOverlayModal],
    shallow
  );

  const { data: overlayBusyDates, isError } = trpc.viewer.availability.calendarOverlay.useQuery(
    {
      loggedInUsersTz: timezone || "Europe/London",
      dateFrom: selectedDate,
      dateTo: selectedDate,
      calendarsToLoad: Array.from(set).map((item) => ({
        credentialId: item.credentialId,
        externalId: item.externalId,
      })),
    },
    {
      enabled: hasSession && set.size > 0 && switchEnabled,
    }
  );

  useEffect(
    function refactorMeWithoutEffect() {
      if (!isError) return;
      setSet(new Set([]));
    },
    [isError]
  );

  const { data, isPending } = trpc.viewer.calendars.connectedCalendars.useQuery(
    {
      skipSync: true,
    },
    {
      enabled: !!calendarSettingsOverlay || Boolean(searchParams?.get("overlayCalendar")),
    }
  );

  // For guests, use the busy times fetched from the guest-calendar API.
  // For logged-in users, use the tRPC calendarOverlay query.
  const effectiveOverlayBusyDates = hasSession
    ? overlayBusyDates
    : guestCalendarConnected
    ? (guestBusyDates as any)
    : undefined;

  return {
    overlayBusyDates: effectiveOverlayBusyDates,
    isOverlayCalendarEnabled: switchEnabled || (!hasSession && guestCalendarConnected),
    connectedCalendars: data?.connectedCalendars || [],
    loadingConnectedCalendar: isPending,
    onToggleCalendar: setSet,
  };
};
